(() => {
  const elementName = 'carisma-report-rail';
  if (customElements.get(elementName)) return;

  class CarismaReportRail extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.abort = new AbortController();
      const { signal } = this.abort;
      // Wait for the parser to append the cards when the script is already loaded.
      this.initFrame = requestAnimationFrame(() => {
        if (!signal.aborted) this.initialize(signal);
      });
    }

    initialize(signal) {
      this.track = this.querySelector('[data-report-track]');
      if (!this.track) return;

      this.cards = Array.from(this.track.children).filter(card => card.matches('.carisma-report'));
      this.navigation = this.querySelector('[data-report-navigation]');
      this.previous = this.querySelector('[data-report-prev]');
      this.next = this.querySelector('[data-report-next]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      const on = (target, event, callback, options = {}) => {
        target?.addEventListener(event, callback, { ...options, signal });
      };
      const scheduleRefresh = () => {
        if (this.refreshFrame || signal.aborted) return;
        this.refreshFrame = requestAnimationFrame(() => {
          this.refreshFrame = 0;
          if (!signal.aborted) this.refresh();
        });
      };

      on(this.track, 'scroll', scheduleRefresh, { passive: true });
      on(window, 'resize', scheduleRefresh, { passive: true });
      on(this.previous, 'click', () => this.step(-1));
      on(this.next, 'click', () => this.step(1));
      on(this.track, 'focusin', event => this.revealFocus(event.target));
      on(this.track, 'pointerdown', event => this.startDrag(event));
      on(window, 'pointermove', event => this.moveDrag(event), { passive: false });
      on(window, 'pointerup', event => this.endDrag(event));
      on(window, 'pointercancel', event => this.endDrag(event));
      on(window, 'blur', () => this.endDrag());
      on(this.track, 'lostpointercapture', event => this.endDrag(event));
      on(this.track, 'dragstart', event => {
        if (this.drag) event.preventDefault();
      });
      on(this.track, 'click', event => {
        // Keyboard clicks have detail 0 and must always keep working.
        if (!this.suppressClick || event.detail === 0) return;
        this.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      }, { capture: true });

      if ('ResizeObserver' in window) {
        this.resize = new ResizeObserver(scheduleRefresh);
        this.resize.observe(this.track);
        this.cards.forEach(card => this.resize.observe(card));
      }
      if (document.fonts) document.fonts.ready.then(scheduleRefresh);
      this.refresh();
    }

    refresh() {
      this.rtl = getComputedStyle(this.track).direction === 'rtl';
      this.maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = this.rtl ? -this.track.scrollLeft : this.track.scrollLeft;
      this.overflowing = this.cards.length > 1 && this.maximum > 1;
      this.dataset.reportOverflow = String(this.overflowing);
      if (this.navigation) this.navigation.hidden = !this.overflowing;
      if (this.previous) this.previous.disabled = !this.overflowing || position <= 1;
      if (this.next) this.next.disabled = !this.overflowing || position >= this.maximum - 1;
    }

    step(direction) {
      if (!this.overflowing) return;
      const width = this.cards[0]?.getBoundingClientRect().width || this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({
        left: direction * (this.rtl ? -1 : 1) * (width + gap),
        behavior: this.motion.matches ? 'auto' : 'smooth'
      });
    }

    revealFocus(target) {
      const card = target.closest('.carisma-report');
      if (!card || !this.track.contains(card)) return;
      const viewport = this.track.getBoundingClientRect();
      const bounds = card.getBoundingClientRect();
      // Scroll just this rail, without moving the surrounding product page.
      const delta = bounds.left < viewport.left ? bounds.left - viewport.left
        : bounds.right > viewport.right ? bounds.right - viewport.right : 0;
      if (delta) this.track.scrollBy({ left: delta, behavior: 'auto' });
    }

    startDrag(event) {
      this.suppressClick = false;
      clearTimeout(this.clickTimer);
      if (!this.overflowing || event.pointerType !== 'mouse' || event.button !== 0
        || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey
        || event.target.closest('button, input, textarea, select, [contenteditable="true"]')) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.track.scrollLeft, moved: false };
    }

    moveDrag(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      const dx = event.clientX - this.drag.x;
      if (!this.drag.moved) {
        if (Math.abs(dx) <= 6 || Math.abs(dx) < Math.abs(event.clientY - this.drag.y)) return;
        this.drag.moved = true;
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      this.track.scrollLeft = this.drag.left - dx;
    }

    endDrag(event) {
      if (!this.drag || (event && event.pointerId !== this.drag.id)) return;
      const drag = this.drag;
      this.drag = null;
      if (drag.moved) {
        this.suppressClick = true;
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 400);
      }
      if (this.track.hasPointerCapture(drag.id)) this.track.releasePointerCapture(drag.id);
      this.track.classList.remove('is-dragging');
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.initFrame);
      cancelAnimationFrame(this.refreshFrame);
      clearTimeout(this.clickTimer);
      if (this.drag && this.track?.hasPointerCapture(this.drag.id)) this.track.releasePointerCapture(this.drag.id);
      this.track?.classList.remove('is-dragging');
      this.drag = null;
      this.refreshFrame = 0;
      this.suppressClick = false;
    }
  }

  customElements.define(elementName, CarismaReportRail);
})();
